import React from 'react';

export interface CardProps {
  /** Card content */
  children: React.ReactNode;
  /** Card variant */
  variant?: 'default' | 'form' | 'info' | 'hero';
  /** Additional CSS classes */
  className?: string;
  /** Click handler */
  onClick?: () => void;
  /** Hover effect */
  hoverable?: boolean;
}

/**
 * Card component from AgroBook design system
 *
 * Variants:
 * - default: Standard card with green border
 * - form: Card for forms with padding
 * - info: Minimal card for displaying information
 * - hero: Gradient hero card for detail pages
 */
export const Card: React.FC<CardProps> = ({
  children,
  variant = 'default',
  className = '',
  onClick,
  hoverable = false,
}) => {
  const baseStyles = 'rounded bg-white transition-agrobook duration-agrobook';

  const variantStyles = {
    default: 'border-thick border-green-200 p-4',
    form: 'border-thick border-green-200 p-6',
    info: 'border-thick border-green-200 overflow-hidden',
    hero: 'bg-gradient-to-br from-green-800 to-green-700 p-7 text-white',
  };

  const hoverStyles = hoverable
    ? 'cursor-pointer hover:border-green-600 hover:shadow-md hover:-translate-y-px'
    : '';

  const clickableStyles = onClick ? 'cursor-pointer' : '';

  return (
    <div
      className={`${baseStyles} ${variantStyles[variant]} ${hoverStyles} ${clickableStyles} ${className}`}
      onClick={onClick}
    >
      {children}
    </div>
  );
};

export interface InfoRowProps {
  /** Icon to display */
  icon: React.ReactNode;
  /** Label text */
  label: string;
  /** Value text or node */
  value: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Info row component for displaying labeled information within info cards
 */
export const InfoRow: React.FC<InfoRowProps> = ({
  icon,
  label,
  value,
  className = '',
}) => {
  return (
    <div className={`flex items-start gap-3 p-3.5 border-b border-green-100 last:border-b-0 ${className}`}>
      <span className="text-lg min-w-5.5 text-green-700 pt-px">{icon}</span>
      <div>
        <div className="text-xs text-gray-500 font-semibold tracking-wider uppercase mb-0.5">
          {label}
        </div>
        <div className="text-md text-gray-900 font-medium">{value}</div>
      </div>
    </div>
  );
};
